import { useEffect, useState } from "react";

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Typography,
  Switch,
  Tooltip,
} from "@mui/material";

import axios from "axios";
import { toast } from "react-toastify";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SweetAlertModal from "../../../components/SweetAlertModal/SweetAlertModal";
import Cancel from "./../../../../assets/icons/close.png";

function EditSectionDelivery({
  open,
  onClose,
  combinedSection,
  onHandleAddItem,
}) {
  const [sections, setSections] = useState([]);
  const [initialSections, setInitialSections] = useState([]);
  const [openCloseConfirmationModal, setOpenCloseConfirmationModal] =
    useState(false);
  const apiAddress = process.env.REACT_APP_SECRET;

  console.log("combinedSection", combinedSection);

  useEffect(() => {
    if (combinedSection) {
      const initialData = combinedSection.map((section) => {
        const name = section.menuTypeName || "";

        const hasDelivery = name.includes("Delivery");
        const hasCollection = name.includes("Collection");

        let startDelivery = false;
        let startCollection = false;

        if ((hasDelivery || hasCollection) && section.restaurantIsDelivery) {
          startDelivery = true;
        } else {
          startDelivery = false;
        }

        if (hasCollection && !hasDelivery) {
          startCollection = true; // Forced Collection Only
        } else if (hasCollection && hasDelivery) {
          startCollection = section.isCollectionOnly;
        } else {
          startCollection = false;
        }

        // Global Override: If Restaurant is forced Collection Only, enforce it
        if (section.restaurantIsCollectionOnly) {
          startCollection = true;
        }

        return {
          id: section.id,
          sectonName: section.sectonName,
          isDeliverableCheck: startDelivery,
          isCollectionCheck: startCollection,
          menuTypeName: name,
          restaurantIsDelivery: section.restaurantIsDelivery || false,
          restaurantIsCollectionOnly:
            section.restaurantIsCollectionOnly || false,
        };
      });

      setSections(initialData);
      setInitialSections(initialData);
    }
  }, [combinedSection]);

  // Check if there are any unsaved changes
  const hasChanges = () => {
    return JSON.stringify(sections) !== JSON.stringify(initialSections);
  };

  const handleDeliverableCheck = (index) => {
    setSections((prevSections) =>
      prevSections.map((sec, i) => {
        if (i === index) {
          const name = sec.menuTypeName || "";
          const hasDelivery = name.includes("Delivery");

          // Prevent toggle if "Delivery" isn't in the name or restaurant doesn't support it
          if (!hasDelivery || !sec.restaurantIsDelivery) {
            return sec;
          }
          return {
            ...sec,
            isDeliverableCheck: !sec.isDeliverableCheck,
          };
        }
        return sec;
      }),
    );
  };

  const handleCollectionCheck = (index) => {
    setSections((prevSections) =>
      prevSections.map((sec, i) => {
        if (i === index) {
          const name = sec.menuTypeName || "";
          const hasDelivery = name.includes("Delivery");
          const hasCollection = name.includes("Collection");

          // Prevent toggle if:
          // 1. Restaurant is globally forced
          // 2. Menu has Collection but NO Delivery (Fixed Collection Only)
          if (
            sec.restaurantIsCollectionOnly ||
            (hasCollection && !hasDelivery)
          ) {
            return sec;
          }
          return {
            ...sec,
            isCollectionCheck: !sec.isCollectionCheck,
          };
        }
        return sec;
      }),
    );
  };

  const handleUpdate = async () => {
    try {
      await Promise.all(
        sections.map(async (section) => {
          const url = `${apiAddress}/api/Section/UpdateSectionIsDelivery?sectionId=${section.id}`;
          const payload = {
            sectionId: section.id,
            isDelivery: section.isDeliverableCheck,
            isCollectionOnly: section.isCollectionCheck,
          };

          await axios.put(url, payload);
        }),
      );

      toast.success("Successfully Updated!", {
        position: "top-right",
        autoClose: 5000,
      });
      setInitialSections(sections); // Update initial state after successful update
      onHandleAddItem(false);
      onClose();
    } catch (error) {
      toast.error("Update failed. Please try again.", {
        position: "top-right",
        autoClose: 5000,
      });
    }
  };

  const resetState = () => {
    setSections(initialSections); // Reset to initial values
    onClose();
  };

  const handleClose = () => {
    if (hasChanges()) {
      setOpenCloseConfirmationModal(true);
    } else {
      resetState();
    }
  };

  return (
    <Dialog fullWidth maxWidth="sm" open={open} onClose={handleClose}>
      <SweetAlertModal
        headerText="Do you want to leave without saving?"
        bodyText="All changes made will be lost"
        open={openCloseConfirmationModal}
        onyes={() => {
          setOpenCloseConfirmationModal(false);
          resetState();
        }}
        close={() => setOpenCloseConfirmationModal(false)}
        id={2}
      />

      <DialogTitle>
        <Grid container justifyContent="space-between" alignItems="center">
          <span style={{ color: "#2a7fd4" }}>GoTaste - Edit Delivery</span>
          <Grid item xs={6}>
            <DialogActions>
              <Button onClick={handleClose}>
                <img src={Cancel} alt="Close" style={{ width: "1.2rem" }} />
              </Button>
            </DialogActions>
          </Grid>
        </Grid>
      </DialogTitle>

      <DialogContent style={{ backgroundColor: "#f3f0f3" }}>
        {sections.map((section, index) => {
          const name = section.menuTypeName || "";
          const hasDelivery = name.includes("Delivery");
          const hasCollection = name.includes("Collection");

          const isDeliveryDisabled =
            !hasDelivery || !section.restaurantIsDelivery;

          const showCollectionToggle = hasCollection;

          const isCollectionDisabled =
            section.restaurantIsCollectionOnly ||
            (hasCollection && !hasDelivery);

          return (
            <Grid
              container
              key={section.id}
              spacing={2}
              sx={{ padding: "0.5rem", marginBottom: "1rem" }}
            >
              <Grid item xs={12}>
                <Typography
                  fontSize={16}
                  sx={{ display: "flex", alignItems: "center", gap: "0.5rem" }}
                >
                  <h6>{section.sectonName}</h6>
                  {isDeliveryDisabled && (
                    <Tooltip title="The menu type is not Delivery" arrow>
                      <InfoOutlinedIcon fontSize="small" color="error" />
                    </Tooltip>
                  )}
                </Typography>

                <Grid container alignItems="center">
                  <Grid
                    item
                    xs={4}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.5rem",
                    }}
                  >
                    <Typography fontSize={16}>Is Deliverable</Typography>
                    <Tooltip
                      title={
                        !section.restaurantIsDelivery
                          ? "Restaurant does not support delivery."
                          : !hasDelivery // <--- USE THE VARIABLE WE ALREADY CALCULATED
                            ? "Menu type does not support delivery."
                            : ""
                      }
                      arrow
                    >
                      <span>
                        <Switch
                          checked={section.isDeliverableCheck}
                          onChange={() => handleDeliverableCheck(index)}
                          inputProps={{ "aria-label": "controlled" }}
                          size="small"
                          disabled={isDeliveryDisabled}
                        />
                      </span>
                    </Tooltip>
                  </Grid>
                </Grid>

                {/* ONLY SHOW THIS BLOCK IF showCollectionToggle IS TRUE */}
                {showCollectionToggle && (
                  <Grid
                    container
                    alignItems="center"
                    sx={{ paddingLeft: "2rem", marginTop: "0.5rem" }}
                  >
                    <Grid
                      item
                      xs={4}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.5rem",
                      }}
                    >
                      <Typography fontSize={16}>Collection Only</Typography>

                      <Tooltip
                        title={
                          section.restaurantIsCollectionOnly
                            ? "Restaurant is collection only."
                            : hasCollection && !hasDelivery
                              ? "Menu type is Collection Only."
                              : ""
                        }
                        arrow
                      >
                        <span>
                          <Switch
                            checked={section.isCollectionCheck}
                            onChange={() => handleCollectionCheck(index)}
                            size="small"
                            // USE THE VARIABLE WE CREATED
                            disabled={isCollectionDisabled}
                          />
                        </span>
                      </Tooltip>
                    </Grid>
                  </Grid>
                )}
              </Grid>
            </Grid>
          );
        })}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} color="primary">
          Close
        </Button>
        <Button color="primary" onClick={handleUpdate}>
          Update
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default EditSectionDelivery;
